import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';

/**
 * Listens for a DOM event on the editor's content element, re-attaching if the element changes.
 * - The latest `handler` is always called, so callers needn't memoise it.
 */
export const useRootEvent = <K extends keyof HTMLElementEventMap>(type: K, handler: (event: HTMLElementEventMap[K]) => void, enabled = true) => {
  const [editor] = useLexicalComposerContext();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const listener = (event: HTMLElementEventMap[K]) => handlerRef.current(event);

    return editor.registerRootListener((root) => {
      if (!root) return;
      root.addEventListener(type, listener);
      return () => root.removeEventListener(type, listener);
    });
  }, [editor, enabled, type]);
};
